import isTouchDevice from "@/utils/isTouchDevice.js";

// Плавная прокрутка страницы. Скроллбар остаётся нативным, а контент догоняет его
// позицию с затуханием: чем дальше от цели, тем быстрее едет, у цели плавно встаёт.
//
// Элемент виджета вынимается из потока (класс .smooth-scroll-content в main.scss)
// и двигается трансформом, высоту документа за него держит распорка. Из-за
// трансформа внутри элемента перестаёт работать position: fixed — оверлеи вроде
// меню и плавающих кнопок должны лежать снаружи.
//
// Свои оверлеи мы так и кладём, а модалки сторонних виджетов (расписание Fitbase)
// встают прямо в свою разметку внутри контента: fixed-окно растягивается на всю
// высоту страницы и открывается у её верха, вне экрана. Поэтому, пока на body висит
// бутстраповский modal-open, контент сдвигаем через top вместо трансформа — сдвиг
// тот же, но точкой отсчёта для fixed снова служит окно. Прокрутка под модалкой
// заблокирована, так что top, который дорог при анимации, тут ничего не стоит.

// Доля оставшегося до цели расстояния, которую контент проходит за кадр при 60 fps
const DEFAULT_EASE = 0.12;
// Длина кадра при 60 fps: к ней приводим шаг на других частотах экрана
const FRAME_DURATION = 1000 / 60;
// Ближе этого расстояния дотягиваем сразу, чтобы не тратить кадры на дрожь в доли пикселя
const SNAP_DISTANCE = 0.05;
// Кадр длиннее — значит вкладка была в фоне, такой промежуток не отыгрываем
const MAX_FRAME_DURATION = 100;
// Столько ждём, пока закрывшееся меню снимет блокировку прокрутки (debounce в noScroll — 30 мс)
const UNLOCK_DELAY = 50;
// Под этим префиксом храним позицию прокрутки, чтобы вернуть её после перезагрузки
const STORAGE_PREFIX = "_smooth_scroll_pos:";
// События, после которых считаем, что страницей управляет пользователь, а не мы
const USER_EVENTS = ["wheel", "keydown", "mousedown", "touchstart"];
// Класс, который ставит на body бутстраповская модалка, — так её открывает Fitbase
const MODAL_OPEN_CLASS = "modal-open";

export default function smoothScroll(element, data) {
    // У тача своя инерция, перехватывать её нельзя; reduce motion — прямая просьба не анимировать
    if (isTouchDevice() || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    const ease = Math.min(Math.max(Number(data.ease) || DEFAULT_EASE, 0.01), 1);

    let position = window.scrollY; // где контент сейчас
    let target = position;         // куда его тянет скроллбар
    let lastTimestamp = 0;
    let animationId = null;
    let spacerHeight = -1;
    let userMoved = false;
    // Куда мы сами поставили страницу на старте: если позиция с тех пор уехала,
    // значит её двигали, и лезть со своим восстановлением уже нельзя
    let appliedPosition = null;
    // Якорь, к которому уже уехали: тот же vue-router переписывает хеш прямо на
    // загрузке, и без этого его hashchange утащил бы страницу от места обновления
    let handledHash = "";
    // Контент сдвинут через top, а не трансформом (пока открыта сторонняя модалка)
    let frozen = false;

    // Распорка отдаёт документу высоту вынутого из потока контента
    const spacer = document.createElement("div");
    spacer.className = "smooth-scroll-spacer";
    spacer.setAttribute("aria-hidden", "true");
    document.body.append(spacer);

    // Распорка получает высоту до того, как контент уйдёт из потока: иначе документ
    // на мгновение схлопнется до высоты окна и браузер обрежет прокрутку до нуля —
    // ровно это и сбрасывало страницу наверх при открытии с якорем и при обновлении
    syncHeight();
    element.classList.add("smooth-scroll-content");

    // Нативная плавность по якорям сложилась бы с нашей и превратилась в кисель
    document.documentElement.style.scrollBehavior = "auto";

    USER_EVENTS.forEach(function (name) {
        window.addEventListener(name, () => (userMoved = true), {
            passive: true,
            once: true
        });
    });

    handledHash = hashId(window.location.hash);
    restoreStartPosition();

    function syncHeight() {
        const height = Math.round(element.getBoundingClientRect().height);

        if (height !== spacerHeight) {
            spacerHeight = height;
            spacer.style.height = height + "px";
        }
    }

    function render() {
        if (frozen) {
            element.style.transform = "none";
            element.style.top = `${-position}px`;
        } else {
            element.style.transform = `translate3d(0, ${-position}px, 0)`;
            element.style.top = "";
        }
    }

    // Модалка могла открыться посреди анимации — доезжать через top значило бы
    // пересчитывать раскладку на каждый кадр, поэтому сразу встаём на место
    function syncFrozen() {
        const shouldFreeze = document.body.classList.contains(MODAL_OPEN_CLASS);

        if (shouldFreeze === frozen) {
            return;
        }

        frozen = shouldFreeze;
        element.classList.toggle("smooth-scroll-frozen", frozen);
        stop();
        target = window.scrollY;
        position = target;
        render();
    }

    function animate(timestamp) {
        const deltaTime = lastTimestamp
            ? Math.min(timestamp - lastTimestamp, MAX_FRAME_DURATION)
            : FRAME_DURATION;
        lastTimestamp = timestamp;

        const distance = target - position;

        if (Math.abs(distance) < SNAP_DISTANCE) {
            position = target;
            render();
            stop();
            return;
        }

        // Экспоненциальное затухание: за кадр съедаем долю оставшегося пути, так что
        // скорость пропорциональна расстоянию до цели. Степень приводит шаг к реальной
        // длине кадра — на 144 Гц контент едет так же, как на 60
        position += distance * (1 - Math.pow(1 - ease, deltaTime / FRAME_DURATION));
        render();

        animationId = requestAnimationFrame(animate);
    }

    function start() {
        if (animationId === null) {
            lastTimestamp = 0;
            animationId = requestAnimationFrame(animate);
        }
    }

    function stop() {
        if (animationId !== null) {
            cancelAnimationFrame(animationId);
            animationId = null;
        }
        lastTimestamp = 0;
    }

    // Позиция якоря в документе: на экране он смещён вверх на текущий сдвиг контента
    function anchorTop(anchor) {
        return anchor.getBoundingClientRect().top + position;
    }

    function scrollToAnchor(anchor) {
        window.scrollTo(0, Math.max(anchorTop(anchor), 0));
    }

    // Перенос без анимации: и скроллбар, и контент сразу оказываются на месте
    function jumpTo(top) {
        syncHeight();

        const limit = Math.max(spacerHeight - window.innerHeight, 0);

        stop();
        window.scrollTo(0, Math.min(Math.max(top, 0), limit));
        position = window.scrollY;
        target = position;
        render();
    }

    // Виджет расписания (fitbase) поднимает vue-router в hash-режиме и переписывает
    // «#about» в «#/about» — принимаем и такой вид адреса
    function hashId(hash) {
        return decodeURIComponent(hash.replace(/^#\/?/, ""));
    }

    function findAnchor(hash) {
        const id = hashId(hash);

        if (!id) {
            return null;
        }

        const anchor = document.getElementById(id) || document.getElementsByName(id)[0];

        return anchor && element.contains(anchor) ? anchor : null;
    }

    function storageKey() {
        // Позиция своя у каждой страницы, но не у каждого якоря
        return STORAGE_PREFIX + window.location.pathname + window.location.search;
    }

    function savePosition() {
        try {
            window.sessionStorage.setItem(storageKey(), String(Math.round(window.scrollY)));
        } catch {}
    }

    function readPosition() {
        try {
            const saved = window.sessionStorage.getItem(storageKey());

            return saved !== null && Number.isFinite(Number(saved)) ? Number(saved) : null;
        } catch {
            return null;
        }
    }

    // Страховка поверх штатного восстановления прокрутки: браузер возвращает позицию,
    // пока документ дорастает до нужной высоты, а у нас он вырастает рывком вместе с
    // распоркой — момент можно и не поймать. Значение то же, что вернул бы браузер,
    // так что если он успел, это просто ничего не меняет
    function restoreStartPosition() {
        const navigation = performance.getEntriesByType("navigation")[0];
        const isReturn = !navigation || navigation.type === "reload" || navigation.type === "back_forward";
        const saved = isReturn ? readPosition() : null;

        if (saved !== null) {
            jumpTo(saved);
            appliedPosition = Math.round(window.scrollY);
            return;
        }

        const anchor = findAnchor(window.location.hash);

        if (anchor) {
            jumpTo(anchorTop(anchor));
            appliedPosition = Math.round(window.scrollY);
        }
    }

    window.addEventListener("scroll", function () {
        target = window.scrollY;
        start();
    }, {
        passive: true
    });

    window.addEventListener("resize", syncHeight);

    new MutationObserver(syncFrozen).observe(document.body, {
        attributes: true,
        attributeFilter: ["class"]
    });

    // Контент меняет высоту сам по себе: догружаются картинки, раскрываются блоки
    if (window.ResizeObserver) {
        new ResizeObserver(syncHeight).observe(element);
    }

    // К моменту load картинки и шрифты уже сдвинули контент, и посчитанная при
    // инициализации позиция могла уехать. Пока страницу не трогали — поправляем
    window.addEventListener("load", function () {
        // Сверяем позицию с той, что поставили сами: если страницу с тех пор двигали
        // (не только колесом — ещё и скриптом, и якорным восстановлением браузера),
        // возвращать её к старту уже неправильно
        if (!userMoved && appliedPosition !== null && Math.round(window.scrollY) === appliedPosition) {
            restoreStartPosition();
        }
    });

    window.addEventListener("pagehide", savePosition);

    // Возврат из bfcache: заново виджет не инициализируется, а скроллбар может
    // приехать не туда — подтягиваем контент под сохранённую позицию
    window.addEventListener("pageshow", function (event) {
        if (event.persisted) {
            const saved = readPosition();

            jumpTo(saved === null ? window.scrollY : saved);
        }
    });

    // Браузер ищет якоря по позиции в документе, а весь контент теперь стоит в его
    // нуле — куда прокручивать, считаем сами
    document.addEventListener("click", function (event) {
        const link = event.target.closest("a[href*='#']");

        if (!link || link.target === "_blank") {
            return;
        }

        // Якорь на другой странице — обычный переход
        if (link.pathname !== window.location.pathname || link.search !== window.location.search) {
            return;
        }

        const anchor = findAnchor(link.hash);

        if (!anchor) {
            return;
        }

        event.preventDefault();
        handledHash = hashId(link.hash);
        window.history.pushState(null, "", link.hash);

        // Меню закрывается этим же кликом, но блокировку прокрутки снимает не сразу,
        // а до этого window.scrollTo ничего не сделает
        if (document.body.classList.contains("no-scroll")) {
            setTimeout(() => scrollToAnchor(anchor), UNLOCK_DELAY);
        } else {
            scrollToAnchor(anchor);
        }
    });

    // Кнопки «назад-вперёд» по якорям — тот же случай, что и клик по ссылке
    window.addEventListener("hashchange", function () {
        if (hashId(window.location.hash) === handledHash) {
            return;
        }

        handledHash = hashId(window.location.hash);

        const anchor = findAnchor(window.location.hash);

        if (anchor) {
            scrollToAnchor(anchor);
        }
    });

    render();
}
